import { useEffect, useMemo } from "react";
import { useContract } from "../ContractContext";
import { useContractUrls } from "../routing/ContractUrls";
import { useHistory } from "react-router-dom";
import FlowLoadingPage from "../../shared/FlowLoadingPage";
import { ImmutableClient, useGetContractPath } from "../../api/ContractClient";
import { useCustomer } from "../../auth/CustomerProvider";
import ErrorPage from "../../shared/error/ErrorPage";
import MemberContractPage from "./MemberContractPage";
import SelectTypePage from "./SelectTypePage";
import StandardContractPage from "./StandardContractPage";
import SimpleContractPage from "./SimpleContractPage";
import { ContractType, ContractViewModel } from "../model/ContractViewModel";
import { useNextUrl, usePrevUrl } from "../routing/ContractFormRoutes";
import { ContractPath } from "../routing/ContractPath";
import { ContractValidationResult } from "../validation/ContractValidation";
import PremiumContractPage from "./PremiumContractPage";
import { GetContractTypePaymentResponse } from "./ContractTypePaymentProcessingPage";
import { usePaymentStatus } from "../../payments/customer/PaymentStatus";

export interface ContractUpgradeProduct {
  id: string;
  price: number;
  taxAmount: number;
  name: string;
  contractType: ContractType;
  shortName: string;
  isFree: boolean;
  features: ProductFeatures;
}

export interface ProductFeatures {
  trialMembership: boolean;
  bankIdSignature: boolean;
  ownershipTransfer: boolean;
  paymentRequest: boolean;
}

export interface ValidatedUpgradeProducts {
  product: ContractUpgradeProduct;
  validation: ContractValidationResult;
  canPurchase: boolean;
  isCompleted: boolean;
}

export interface GetContractUpgradeProductsResponse {
  upgrades: ValidatedUpgradeProducts[];
  payment: GetContractTypePaymentResponse | null;
  canPurchase: boolean;
  isCompleted: boolean;
}

export function hasFeature(
  contract: ContractViewModel,
  upgrade: GetContractUpgradeProductsResponse,
  check: (features: ProductFeatures) => boolean
) {
  const contractType = contract.contractType?.current;
  if (contractType === ContractType.Member) return true;
  const contractTypeProduct = upgrade.upgrades.find(
    (x) => x.product.contractType === contractType
  )?.product;
  if (!contractTypeProduct) return false;
  return check(contractTypeProduct.features);
}

export function useContractUpgrade(): ImmutableClient<GetContractUpgradeProductsResponse> {
  const contract = useContract();

  const path = useMemo(
    () => `contract/${contract.id}/contractType/upgrade`,
    [contract.id]
  );

  const client = useGetContractPath<GetContractUpgradeProductsResponse>({
    path,
  });

  const paymentParameters = client.result?.payment
    ? {
        id: client.result.payment.payment.paymentDetails.id,
        signature: client.result.payment.signature,
      }
    : null;

  const paymentStatus = usePaymentStatus(paymentParameters);

  return {
    ...client,
    result: client.result
      ? {
          ...client.result,
          payment: client.result.payment
            ? {
                ...client.result.payment,
                payment: paymentStatus.payment || client.result.payment.payment,
              }
            : null,
        }
      : null,
  };
}

export default function ContractTypePage() {
  const contract = useContract();
  const upgrade = useContractUpgrade();
  const customer = useCustomer();

  const history = useHistory();

  const current = ContractPath.type;
  const urls = useContractUrls();
  const nextUrl = useNextUrl(current) || urls.preview;
  const prevUrl = usePrevUrl(current) || urls.contract;

  useEffect(() => {
    if (nextUrl && !contract.contractType?.isRequired) {
      console.log(
        "Skipping Contract Type because it is not required for this contract."
      );

      history.replace(nextUrl);
    }
  }, [history, nextUrl, contract]);

  const currentType = contract.contractType?.current;
  useEffect(() => {
    if (currentType === ContractType.Simple) window.scrollTo(0, 0);
  }, [currentType]);

  if (upgrade.isLoading) {
    return <FlowLoadingPage />;
  }

  if (upgrade.error || !upgrade.result) {
    return <ErrorPage message={upgrade.error?.message} />;
  }

  if (customer.isLoggedIn && !customer.result) {
    return <FlowLoadingPage />;
  }

  if (customer.isLoggedIn && customer.result && customer.result.qualifiesForMembershipUpgrade) {
    return (
      <MemberContractPage
        prevUrl={prevUrl}
        nextUrl={nextUrl}
        customer={customer.result}
      />
    );
  }

  if (customer.isLoggedIn && customer.error) {
    return <ErrorPage message="Kunne ikke hente kundeobjektet." />;
  }

  switch (currentType) {
    case ContractType.Member:
      return (
        <MemberContractPage
          prevUrl={prevUrl}
          nextUrl={nextUrl}
          customer={customer.result}
        />
      );
    case ContractType.Standard:
      return <StandardContractPage page={current} />;
    case ContractType.Premium:
      return <PremiumContractPage page={current} />;
    case ContractType.Simple:
      return <SimpleContractPage upgrade={upgrade.result} page={current} />;
    default:
      return <SelectTypePage upgrade={upgrade.result} />;
  }
}
